import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { StateManagementService } from '../../services/state-management.service';
import { StripeService } from '../../services/stripe.service';
import { OnboardingApiService } from '../../services/onboarding-api.service';

export type StripeStatus = 'none' | 'pending' | 'incomplete' | 'active' | 'restricted';

@Component({
  selector: 'app-bank-integration',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatChipsModule,
    MatDividerModule,
  ],
  templateUrl: './bank-integration.component.html',
  styleUrl: './bank-integration.component.scss',
})
export class BankIntegrationComponent implements OnInit {
  @Input() organizationId!: string;
  @Output() stepComplete = new EventEmitter<string>();

  stripeStatus: StripeStatus = 'none';
  stripeAccountId: string | null = null;
  isCreatingAccount = false;
  isLoadingStatus = false;
  isRedirecting = false;
  statusDetails: any = null;

  constructor(
    public stateService: StateManagementService,
    private stripeService: StripeService,
    private api: OnboardingApiService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    const state = this.stateService.snapshot;
    this.stripeStatus = (state.stripeStatus as StripeStatus) || 'none';
    this.stripeAccountId = state.stripeAccountId;

    // If we have an account, refresh the status
    if (this.stripeAccountId) {
      this.refreshStatus();
    }
  }

  async createAndConnect(): Promise<void> {
    this.isCreatingAccount = true;
    try {
      // Step 1: Create the Stripe account if it doesn't exist
      if (!this.stripeAccountId) {
        const result = await this.stripeService.createAndSaveAccount(this.organizationId).toPromise();
        this.stripeAccountId = result?.accountId || null;
        this.stripeStatus = 'pending';
      }

      // Step 2: Redirect to Stripe's hosted onboarding
      this.isRedirecting = true;
      await this.stripeService.redirectToOnboarding(this.organizationId).toPromise();
      // If redirect succeeded, the page will navigate away.
    } catch (err: any) {
      this.snackBar.open('Error: ' + (err.message || err), 'Close', { duration: 5000 });
      this.isCreatingAccount = false;
      this.isRedirecting = false;
    }
  }

  async refreshStatus(): Promise<void> {
    if (!this.stripeAccountId) return;
    this.isLoadingStatus = true;
    try {
      const result = await this.stripeService.refreshAccountStatus(this.organizationId).toPromise();
      this.stripeStatus = result?.status as StripeStatus;
      this.statusDetails = result;

      if (this.stripeStatus === 'active') {
        this.snackBar.open('Stripe Connect is active!', 'Close', { duration: 3000 });
      }
    } catch (err: any) {
      this.snackBar.open('Could not refresh status: ' + (err.message || err), 'Close', { duration: 4000 });
    } finally {
      this.isLoadingStatus = false;
    }
  }

  async resumeOnboarding(): Promise<void> {
    this.isRedirecting = true;
    try {
      await this.stripeService.redirectToOnboarding(this.organizationId).toPromise();
    } catch (err: any) {
      this.snackBar.open('Error: ' + (err.message || err), 'Close', { duration: 5000 });
      this.isRedirecting = false;
    }
  }

  onContinue(): void {
    this.stepComplete.emit(this.stripeStatus);
  }

  get statusLabel(): string {
    const labels: Record<string, string> = {
      none: 'Not Connected',
      pending: 'Pending Setup',
      incomplete: 'Incomplete',
      active: 'Active',
      restricted: 'Restricted',
    };
    return labels[this.stripeStatus] || this.stripeStatus;
  }

  get statusColor(): string {
    const colors: Record<string, string> = {
      none: 'default',
      pending: 'warn',
      incomplete: 'warn',
      active: 'primary',
      restricted: 'warn',
    };
    return colors[this.stripeStatus] || 'default';
  }

  get isActive(): boolean {
    return this.stripeStatus === 'active';
  }

  get requiresAction(): boolean {
    return ['none', 'pending', 'incomplete'].includes(this.stripeStatus);
  }

  formatItem(item: string): string {
    return item ? item.replace(/_/g, ' ') : '';
  }
}
